import { useCallback, useEffect, useState, useTransition } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { deleteDocument, getDocumentById, updateDocument } from 'src/shared/services/documentService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Schema } from 'yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Typography } from '@mui/material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { getAllDocumentCategorys } from 'src/shared/services/documentCategoryService';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { DocumentCategory } from '../models/documentCategory';
import { DocumentRequest } from '../models/documentRequest';
import { DocumentCategorySearchFilter } from '../models/documentCategorySearchFilter';
import { DocumentResponse } from '../models/documentResponse';

type DocumentDetailsModalProps = {
  documentId: number;
  clearDokumentId: (clearId: null) => void;
  categoryFilter: DocumentCategorySearchFilter;
  refetchDataGrid: (refetch: boolean) => void;
};

export const DocumentDetailsModal = (props: DocumentDetailsModalProps) => {
  const { documentId, clearDokumentId, categoryFilter, refetchDataGrid } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();

  const [documentCategories, setDocumentCategories] = useState<DocumentCategory[]>();
  const [document, setDocument] = useState<DocumentResponse>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPending, startTransition] = useTransition();

  const schema: Schema<DocumentRequest> = yup.object().shape({
    documentCategory_id: yup.number().required(t('common:validationMessages.required')),
    user_id: yup.string().required(t('common:validationMessages.required')),
    label: yup.string().required(t('common:validationMessages.required')),
    description: yup.string().required(t('common:validationMessages.required')),
    // metaWords: yup.string().required(t('common:validationMessages.required')),
    private: yup.boolean().required(t('common:validationMessages.required')),
  });

  const formMethods = useForm<DocumentRequest>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      document_id: 0,
      label: '',
      documentCategory_id: 0,
      description: '',
      metaWords: '',
      private: true,
      user_id: '',
      fileName: '',
    },
  });

  const { handleSubmit, setValue, watch } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    const res = await getDocumentById(documentId);
    try {
      if (isMounted()) {
        startTransition(() => {
          setDocument(res.data);
          setValue('document_id', res.data.document_id);
          setValue('label', res.data.label);
          setValue('documentCategory_id', res.data.documentCategory_id);
          setValue('description', res.data.description);
          setValue('metaWords', res.data.metaWords);
          setValue('private', res.data.private);
          setValue('user_id', res.data.user_id);
          setValue('fileName', res.data.fileName);
        });
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [documentId, isMounted, setValue]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllDocumentCategorys(categoryFilter);
      if (isMounted()) {
        const allCategories = result.data.results;

        // Skapar en kategori (Övrigt)
        const otherCategory = {
          documentCategory_id: 0,
          category: t('documentArchive:documentCategory.other'),
          description: '',
        };

        const mergedCategories = [otherCategory, ...allCategories];
        setDocumentCategories(mergedCategories);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [categoryFilter, isMounted, t]);

  useEffect(() => {
    if (isMounted()) {
      fetchCategories();
    }
  }, [fetchCategories, isMounted]);

  const onSubmit = async (data: DocumentRequest) => {
    setLoading(true);
    try {
      await updateDocument(data);
      if (isMounted()) {
        toast.success(<>{t('documentArchive:updatedSuccess')}</>);
        refetchDataGrid(true);
        clearDokumentId(null);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteDocument(documentId);
      if (isMounted()) {
        toast.success(<>{t('documentArchive:deletedSuccess')}</>);
        refetchDataGrid(true);
        clearDokumentId(null);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const formId = 'update-file';
  return (
    <FormProvider {...formMethods}>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <GenericDialog
          maxWidth='sm'
          dialogTitle={document?.fileName || ''}
          open={!!documentId}
          onClose={() => clearDokumentId(null)}
          onSave={handleSubmit(onSubmit)}
          dialogContent={
            <>
              <Grid container spacing={2}>
                <LoadingOverlay loading={loading || isPending} />
                <Grid item xs={6}>
                  <AutocompleteDropdown<DocumentCategory>
                    keyProp='documentCategory_id'
                    nameProp='category'
                    value={documentCategories?.find((x) => x.documentCategory_id === Number(watch('documentCategory_id'))) ?? null}
                    label={t('documentArchive:documentCategory.category')}
                    name='documentCategory_id'
                    placeholder={t('documentArchive:documentCategory.other')}
                    options={documentCategories || []}
                    callback={(selectedOption) => {
                      if (selectedOption) {
                        setValue('documentCategory_id', selectedOption.documentCategory_id || 0);
                      } else {
                        setValue('documentCategory_id', 0);
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField name='label' label={t('documentArchive:documentTitle')} />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField disabled label={t('documentArchive:fileName')} name='fileName' />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField label={t('documentArchive:documentCategory.description')} name='description' />
                </Grid>

                <Grid item xs={6}>
                  <ControlledCheckboxNew
                    name='private'
                    customizedLabel={
                      <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('documentArchive:markAsPrivate')}
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </>
          }
          dialogActions={
            <Grid container justifyContent='space-between'>
              <Grid item>
                <Button variant='outlined' color='error' onClick={() => setOpenDeleteDialog(true)}>
                  {t('common:button.delete')}
                </Button>
              </Grid>

              <Grid item>
                <Button form={formId} sx={{ marginLeft: 2 }} disabled={loading} disableElevation variant='contained' color='primary' type='submit'>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Grid>
          }
        />
        <DeleteConfirmationDialog
          title={t('common:confirmDelete')}
          value=''
          modaltext={t('common:irreversibleAction')}
          onClose={(value) => {
            if (value) {
              handleDelete();
            } else {
              setOpenDeleteDialog(false);
            }
          }}
          open={openDeleteDialog}
        />
      </form>
    </FormProvider>
  );
};
